import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";

import { useAppMutation, useAppQuery } from "../../../api/fetchClient";
import { ADD_STATIC_CONTENT, GET_STATIC_CONTENT, UPDATE_STATIC_CONTENT } from "../../../api/url";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { Loader, LanguageTabs, Card, PageHeader, EmptyState, Button } from "../../../components/ui";
import RichTextEditor from "./RichTextEditor";
import { strings } from "../../../strings/strings";

const tabs = [strings.contactUs, strings.privacyPolicy, strings.aboutMG, strings.adverseReporting];

const tabToApiTitle = {
  "Contact us": "contact_us",
  "Privacy Policy": "privacy_policy",
  "About MG": "about_mg",
  "Adverse Reporting": "adverse_reporting",
};

const normalizeTitle = (title) => {
  if (!title) return "";
  return title.toLowerCase().replace(/\s+/g, "_").replace(/&/g, "_and_").trim();
};

const cleanForValidation = (html = "") => {
  if (!html) return "";
  return String(html)
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<p>\s*<\/p>/g, "")
    .replace(/<br\s*\/?>/gi, "")
    .trim();
};

const stripHtmlToText = (html = "") => {
  const raw = String(html)
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\u00a0/g, " ");
  return raw.replace(/\s+/g, " ").trim();
};

const containsArabic = (html = "") => {
  if (!html) return false;
  return /[\u0600-\u06FF]/.test(stripHtmlToText(html));
};

const preserveSpaces = (html = "") => {
  if (!html || typeof document === "undefined") return html;

  const temp = document.createElement("div");
  temp.innerHTML = html;

  const walk = (node) => {

    if (node.nodeType === 3) {
      node.textContent = (node.textContent || "").replace(/ {2,}/g, (spaces) => "\u00A0".repeat(spaces.length));
      return;
    }

    if (node.nodeType === 1) {
      Array.from(node.childNodes).forEach(walk);
    }
  };

  Array.from(temp.childNodes).forEach(walk);
  return temp.innerHTML;
};

const StaticContain = () => {
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [language, setLanguage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editorHtml, setEditorHtml] = useState("");
  const [contentId, setContentId] = useState(null);
  const [aboutMgSubField, setAboutMgSubField] = useState("patient");
  const [patientContent, setPatientContent] = useState("");
  const [hcpContent, setHcpContent] = useState("");
  const [patientContentId, setPatientContentId] = useState(null);
  const [hcpContentId, setHcpContentId] = useState(null);
  const apiBaselineRef = useRef("");
  const baselineCapturedRef = useRef(false);
  const addContentMutation = useAppMutation();
  const updateContentMutation = useAppMutation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [language, activeTab]);

  const normalizeLanguage = useCallback((lang) => {
    if (!lang) return "";
    const lower = lang.toLowerCase().trim();
    if (lower.startsWith("ar") || lower === "arabic") return "arabic";
    return lower;
  }, []);
  const { data, refetch, isLoading } = useAppQuery({
    queryKey: [QUERY_KEYS.STATIC_CONTENT, language, activeTab],
    url: GET_STATIC_CONTENT,
    params: {
      language: language ? language.toLowerCase() : undefined,
      title: tabToApiTitle[activeTab],
    },
    enabled: !!language,
  });

  const currentItem = useMemo(() => {
    const results = Array.isArray(data?.results) ? data.results : [];
    const expectedTitle = tabToApiTitle[activeTab];
    const expectedLang = normalizeLanguage(language);
    if (activeTab === strings.aboutMG) {
      const patient = results.find((c) => {
        const t = normalizeTitle(c?.title);
        const l = normalizeLanguage(c?.language);
        return t === "about_mg" && l === expectedLang && c?.role === "patient";
      });

      const hcp = results.find((c) => {
        const t = normalizeTitle(c?.title);
        const l = normalizeLanguage(c?.language);
        return t === "about_mg" && l === expectedLang && c?.role === "hcp";
      });

      return { patient, hcp };
    }
    return results.find((c) => {
      const t = normalizeTitle(c?.title);
      const expected = normalizeTitle(expectedTitle);
      const l = normalizeLanguage(c?.language);
      return t === expected && l === expectedLang;
    });
  }, [data?.results, activeTab, language, normalizeLanguage]);

  useEffect(() => {
    if (!language) return;

    if (activeTab === strings.aboutMG) {
      const patientApi = typeof currentItem?.patient?.content === "string" ? currentItem.patient.content : "";
      const hcpApi = typeof currentItem?.hcp?.content === "string" ? currentItem.hcp.content : "";

      setPatientContent(patientApi);
      setHcpContent(hcpApi);
      setPatientContentId(currentItem?.patient?.id || null);
      setHcpContentId(currentItem?.hcp?.id || null);

      const chosenContent = aboutMgSubField === "patient" ? patientApi : hcpApi;
      const chosenId = aboutMgSubField === "patient" ? currentItem?.patient?.id || null : currentItem?.hcp?.id || null;

      setEditorHtml(chosenContent);
      setContentId(chosenId);
    } else {
      const apiContent = typeof currentItem?.content === "string" ? currentItem.content : "";
      setEditorHtml(apiContent);
      setContentId(currentItem?.id || null);
    }


    apiBaselineRef.current = "";
    baselineCapturedRef.current = false;
  }, [currentItem, language, activeTab, aboutMgSubField]);

  const captureBaselineIfNeeded = useCallback(
    (html) => {
      if (!baselineCapturedRef.current && isEditing) {
        setTimeout(() => {
          if (!baselineCapturedRef.current && isEditing) {
            apiBaselineRef.current = String(html || "");
            baselineCapturedRef.current = true;
          }
        }, 50);
      }
    },
    [isEditing],
  );

  const hasContentChanged = useCallback(() => {

    if (!baselineCapturedRef.current) {
      if (isEditing && editorHtml) {
        apiBaselineRef.current = String(editorHtml || "").trim();
        baselineCapturedRef.current = true;
        return false;
      }
      return false;
    }

    const current = String(editorHtml || "").trim();
    const baseline = String(apiBaselineRef.current || "").trim();

    if (!current && !baseline) return false;
    return current !== baseline;
  }, [editorHtml, isEditing]);

  const handleAdd = useCallback(() => {
    if (isLoading || !language) return;

    if (!tabToApiTitle[activeTab] && activeTab !== strings.aboutMG) {
      toast.error(strings.selectValidTab);
      return;
    }
    if (!language || language.trim() === "") {
      toast.error(strings.selectLanguage);
      return;
    }

    const rawContent = editorHtml ? String(editorHtml) : "";
    const validationContent = cleanForValidation(rawContent);
    const plainText = stripHtmlToText(validationContent);

    if (!plainText) {
      toast.error(strings.emptyContentError);
      return;
    }

    const languageForApi = language.toLowerCase().trim();

    const payload = {
      title: activeTab === strings.aboutMG ? "about_mg" : tabToApiTitle[activeTab],
      content: rawContent,
      language: languageForApi,
    };

    if (activeTab === strings.aboutMG) {
      payload.role = aboutMgSubField;
    }

    addContentMutation.mutate(
      { url: ADD_STATIC_CONTENT, method: "POST", data: payload },
      {
        onSuccess: (res) => {
          toast.success(res?.message);
          refetch();
          setIsEditing(false);
          if (activeTab === strings.aboutMG) {
            if (aboutMgSubField === "patient") setPatientContent(rawContent);
            else setHcpContent(rawContent);
          }
        },
        onError: (error) => {
          const msg =
            error?.message ||
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            strings.staticContentSaveFailed;
          toast.error(msg);
        },
      },
    );
  }, [isLoading, language, activeTab, editorHtml, addContentMutation, aboutMgSubField, refetch]);

  const handleUpdate = useCallback(() => {
    if (!hasContentChanged()) {
      toast.info(strings.noChangesDetected);
      setIsEditing(false);
      return;
    }

    updateContentMutation.mutate(
      {
        url: `${UPDATE_STATIC_CONTENT}?id=${contentId}`,
        method: "PUT",
        data: { content: editorHtml },
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message);
          refetch();
          setIsEditing(false);
          if (activeTab === strings.aboutMG) {
            if (aboutMgSubField === "patient") setPatientContent(editorHtml);
            else setHcpContent(editorHtml);
          }
        },
        onError: (error) => {
          toast.error(error?.response?.data?.message);
        },
      },
    );
  }, [contentId, editorHtml, hasContentChanged, refetch, updateContentMutation, activeTab, aboutMgSubField]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setIsEditing(false);

    if (tab === strings.aboutMG) {
      setAboutMgSubField("patient");
    }
  }, []);

  const handleLanguageChange = useCallback(
    (langCode) => {
      if (!langCode) return;

      setLanguage(langCode);
      setIsEditing(false);

      if (activeTab === strings.aboutMG) {
        setAboutMgSubField("patient");
      }
    },
    [activeTab],
  );

  const handleSubFieldChange = useCallback(
    (subField) => {
      setAboutMgSubField(subField);
      setIsEditing(false);

      const nextContent = subField === "patient" ? patientContent : hcpContent;
      const nextId = subField === "patient" ? patientContentId : hcpContentId;

      setEditorHtml(nextContent);
      setContentId(nextId);

      apiBaselineRef.current = "";
      baselineCapturedRef.current = false;
    },
    [patientContent, hcpContent, patientContentId, hcpContentId],
  );

  const hasExistingContent =
    currentItem?.content && typeof currentItem.content === "string" && stripHtmlToText(currentItem.content).length > 0;

  const hasAboutMgContent = useCallback(() => {
    if (activeTab !== strings.aboutMG) return false;

    if (aboutMgSubField === "patient") {
      return patientContentId && patientContent && stripHtmlToText(patientContent).length > 0;
    }
    return hcpContentId && hcpContent && stripHtmlToText(hcpContent).length > 0;
  }, [activeTab, aboutMgSubField, patientContentId, patientContent, hcpContentId, hcpContent]);

  const getCurrentContent = useCallback(() => {
    if (activeTab === strings.aboutMG) {
      return aboutMgSubField === "patient" ? patientContent : hcpContent;
    }
    return currentItem?.content;
  }, [activeTab, aboutMgSubField, patientContent, hcpContent, currentItem]);

  const getCurrentContentId = useCallback(() => {
    if (activeTab === strings.aboutMG) {
      return aboutMgSubField === "patient" ? patientContentId : hcpContentId;
    }
    return contentId;
  }, [activeTab, aboutMgSubField, patientContentId, hcpContentId, contentId]);

  const renderPageHeader = () => (
    <>
      <PageHeader title={strings.staticContentTitle} description={strings.staticContentDescription} className="mb-6" />
      <div className="mb-6">
        <LanguageTabs
          activeLanguage={language}
          onLanguageChange={handleLanguageChange}
          variant="buttons"
          useApi={true}
          autoSelectFirst={true}
        />
      </div>
    </>
  );

  if (!language) {
    return (
      <div>
        <div className="max-w-7xl mx-auto">
          {renderPageHeader()}
          <Card>
            <EmptyState message={strings.selectLanguageToView} description={strings.noLanguagesAvailable} />
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <div className="max-w-7xl mx-auto">
          {renderPageHeader()}
          <Card>
            <Loader />
          </Card>
        </div>
      </div>
    );
  }

  const displayHtml = activeTab === strings.aboutMG ? getCurrentContent() : currentItem?.content;
  const isRtl = containsArabic(editorHtml);

  return (
    <div>
      <div className="max-w-7xl mx-auto">
        {renderPageHeader()}

        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`whitespace-nowrap px-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? "border-primary text-primary"
                    : "border-transparent text-secondary hover:text-primary"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <Card>
          {isEditing ? (
            <RichTextEditor
              value={editorHtml}
              onChange={(html) => {
                const next = html || "";
                setEditorHtml(next);
                captureBaselineIfNeeded(next);
              }}
              handleAdd={handleAdd}
              hasContentChanged={hasContentChanged()}
              handleUpdate={handleUpdate}
              isRtl={isRtl}
              item={activeTab === strings.aboutMG ? { id: getCurrentContentId() } : currentItem}
              onCancel={() => setIsEditing(false)}
              isLoading={addContentMutation.isPending || updateContentMutation.isPending}
            />
          ) : (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex  items-center justify-between">
                <div className="flex gap-2 items-center">
                  <h3 className="text-base font-semibold text-secondary">{activeTab}</h3>
                  <p className="text-xs text-secondary font-normal">({language?.toUpperCase() || ""})</p>
                </div>

                <Button
                  onClick={() => {
                    const content =
                      activeTab === strings.aboutMG
                        ? getCurrentContent()
                        : typeof currentItem?.content === "string"
                          ? currentItem.content
                          : "";

                    setEditorHtml(content);
                    baselineCapturedRef.current = false;
                    setIsEditing(true);
                  }}
                  variant="primary"
                  size="sm"
                >
                  {activeTab === strings.aboutMG
                    ? hasAboutMgContent()
                      ? strings.edit
                      : strings.add
                    : hasExistingContent
                      ? strings.edit
                      : strings.add}
                </Button>
              </div>

              {activeTab === strings.aboutMG && (
                <div className="px-6 pt-4 border-b border-gray-200">
                  <div className="flex gap-4">
                    <button
                      onClick={() => handleSubFieldChange("patient")}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        aboutMgSubField === "patient"
                          ? "border-primary text-primary"
                          : "border-transparent text-secondary hover:text-primary"
                      }`}
                    >
                      {strings.patient}
                    </button>
                    <button
                      onClick={() => handleSubFieldChange("hcp")}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        aboutMgSubField === "hcp"
                          ? "border-primary text-primary"
                          : "border-transparent text-secondary hover:text-primary"
                      }`}
                    >
                      {strings.hcp}
                    </button>
                  </div>
                </div>
              )}

              <div className="ql-snow static-content-display w-full max-w-full bg-white border border-gray-200">
                <div className="p-6">
                  <div
                    className="ql-editor"
                    dir={containsArabic(displayHtml) ? "rtl" : "ltr"}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(preserveSpaces(displayHtml) || strings.noChangesDetected),
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default StaticContain;
